import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import ReactTooltip from 'react-tooltip';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import Utils from 'common/services/Utils';
import { TopVehicleCostDashboardDto, TopVehicleCostsDashboardDto } from 'api/dashboard/dtos/TopVehicleCostDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import useWidgetFilters from '../hooks/useWidgetFilters';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import useInfiniteReveal from '../components/useInfiniteReveal';
import KpiHeader from '../components/KpiHeader';
import { getPreviousRange, PreviousRange } from '../utils/getPreviousRange';
import styles from './TopVehicleCosts.module.scss';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import WidgetNoData from '../components/WidgetNoData';
import infiniteStyles from '../components/useInfiniteReveal.module.scss';

const PAGE_SIZE = 10;

const SCALE_COLORS = [
	'#025a7a', // darkest — top rank
	'#038cb6',
	'#02c7c7',
	'#6de0e0',
	'#b8f0f0', // lightest — 5th+
];

const TopVehicleCosts = () => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [data, setData] = useState<TopVehicleCostsDashboardDto | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [previousTotal, setPreviousTotal] = useState<number | null>(null);
	const [previousRange, setPreviousRange] = useState<PreviousRange | null>(null);
	const { dateFrom, dateTo, setDateRange } = useWidgetFilters('topVehicleCosts');

	useEffect(() => {
		let cancelled = false;
		const fetchData = async () => {
			setIsLoading(true);
			setPreviousTotal(null);
			setPreviousRange(null);
			try {
				const result = await DashboardService.getTopVehicleCosts({ dateFrom, dateTo });
				if (!cancelled) setData(result);
			} catch {
				if (!cancelled) addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			} finally {
				if (!cancelled) setIsLoading(false);
			}

			try {
				const range = getPreviousRange(dateFrom, dateTo);
				const previous = await DashboardService.getTopVehicleCosts({ dateFrom: range.prevFrom, dateTo: range.prevTo });
				if (!cancelled) {
					setPreviousTotal(previous?.grandTotal ?? 0);
					setPreviousRange(range);
				}
			} catch {
				if (!cancelled) {
					setPreviousTotal(null);
					setPreviousRange(null);
				}
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [dateFrom, dateTo]);

	const items = data?.items || [];
	const maxCost = Math.max(...items.map(d => d.totalCost), 0);
	const grandTotal = data?.grandTotal ?? 0;
	const { visibleItems, sentinelRef, hasMore } = useInfiniteReveal(items, PAGE_SIZE);

	const buildVehicleTooltip = (item: TopVehicleCostDashboardDto) =>
		[item.vehicleBrandName, item.vehicleModelName].filter(Boolean).join(' ') || undefined;

	const goToVehicleMonthlyCharges = (vehicleId: string) => {
		try {
			localStorage.setItem('vehicle-monthly-charges-filters', JSON.stringify({
				startDate: dateFrom,
				endDate: dateTo,
			}));
		} catch {
			// ignore
		}
		window.open(`/vehicles/details/${vehicleId}/monthlyCharges/list`, '_blank');
	};

	const goToVehiclesManagement = () => {
		try {
			// inactives: false — dashboard agrega só viaturas ativas; clique mantém o critério.
			const raw = localStorage.getItem('VEHICLESMANAGEMENTFILTER');
			const existing = raw ? JSON.parse(raw) : {};
			const next = {
				...existing,
				costsStartDate: dateFrom,
				costsEndDate: dateTo,
				inactives: false,
				page: 1,
				scrollToItemId: undefined,
			};
			localStorage.setItem('VEHICLESMANAGEMENTFILTER', JSON.stringify(next));
		} catch {
			// ignore
		}
		window.open('/vehicles-management', '_blank');
	};

	return (
		<Box className={tableStyles.container} title={t('dashboard.top_vehicle_costs') as string}
			subTitleTooltip={t('dashboard.top_vehicle_costs_tooltip' as any) as string}
			renderOptions={<WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} />}>
			{isLoading ? (
				<div className={tableStyles.centerAlign}><LoadingSpinner /></div>
			) : !data || items.length === 0 ? (
				<WidgetNoData />
			) : (
				<>
					<KpiHeader
						value={Utils.formatCurrency(grandTotal)}
						subLabel={t('dashboard.total_cost' as any) as string}
						currentTotal={grandTotal}
						previousTotal={previousTotal}
						previousRange={previousRange}
						onClick={goToVehiclesManagement}
					/>
					<div className={styles.list}>
					{visibleItems.map((item, index) => {
						const barWidth = maxCost > 0 ? (item.totalCost / maxCost) * 100 : 0;
						const color = SCALE_COLORS[Math.min(index, SCALE_COLORS.length - 1)];
						return (
							<div
								key={item.vehicleId}
								className={styles.row}
								onClick={() => goToVehicleMonthlyCharges(item.vehicleId)}
							>
								<span className={styles.plate}><span data-tip={buildVehicleTooltip(item)} data-for="top-vehicle-costs-tooltip" data-place="right">{item.registrationNumber}</span></span>
								<div className={styles.barTrack}>
									<div className={styles.barFill} style={{ width: `${barWidth}%`, background: color }} />
								</div>
								<span className={styles.cost}>
									{Utils.formatCurrency(item.totalCost)}
								</span>
							</div>
						);
					})}
					{hasMore && <div ref={sentinelRef} className={infiniteStyles.sentinel} />}
					</div>
					<ReactTooltip id="top-vehicle-costs-tooltip" className={styles.tooltip} effect="solid" multiline={true} />
				</>
			)}
		</Box>
	);
};

export default TopVehicleCosts;
